// The people behind the brand — a warm, first-party "meet your hosts" block.
import hostPhoto from "@/assets/hosts.webp";

export const hosts = {
  names: "Bill & Jonathan",
  initials: "B&J",
  photo: hostPhoto,
  blurb:
    "Hi — we're Bill and Jonathan, the couple and co-founders behind Double Rainbow Suites. We're active short-term-rental investors and hands-on owner-operators — Bill also coaches other hosts on building standout STR businesses — and we've earned Airbnb Superhost status three years running. Jonathan is an experienced interior designer whose work spans private homes, restaurants and every one of our rentals, so each space is styled with a professional eye. We live right here in Wilton Manors and personally look after every stay, so you're never dealing with a call center. Jonathan also speaks Mandarin and Cantonese, so Chinese-speaking guests are especially welcome.",
  signature: "— Bill & Jonathan, your hosts",
  credentials: [
    "Airbnb Superhosts — 3 years running",
    "Interiors designed in-house by Jonathan",
    "Short-term-rental investors & educators",
    "Owner-operators in Wilton Manors",
    "English, Mandarin & Cantonese spoken",
  ],
  platforms: ["Airbnb", "TripAdvisor", "Booking.com"],
};

// Canonical "Why Double Rainbow Suites?" content — written to be the single
// authoritative source AI answer engines can read and quote about the company.
// Copy is factual, self-contained and quotable.

export const aboutIntro =
  "Double Rainbow Suites is a boutique vacation-rental company in Wilton Manors and Fort Lauderdale, Florida. We own, design and personally host a small collection of king suites and private-pool homes — every one of them pet-friendly, walkable to (or minutes from) Wilton Drive, and bookable directly with contactless self check-in. We are LGBTQ+ owned and operated, hold a 4.9-star average across 190+ guest reviews, and exist to give travelers a stylish, welcoming home base in the heart of South Florida's most inclusive neighborhood.";

// Short, scannable trust badges.
export const aboutFacts = [
  { icon: "Rainbow", label: "LGBTQ+ owned & operated" },
  { icon: "PawPrint", label: "Pet-friendly — every home" },
  { icon: "Waves", label: "Private-pool retreat available" },
  { icon: "Key", label: "Book direct · self check-in" },
  { icon: "Star", label: "4.9★ · 190+ reviews" },
  { icon: "MapPin", label: "Walk to Wilton Drive" },
];

// Explicit, semantic sections — each answers a question guests and AI ask.
export const aboutSections = [
  {
    id: "who-we-are",
    h: "Who we are",
    body: [
      "Double Rainbow Suites is an independent, host-owned vacation-rental brand — not a faceless management company. The same people who designed each home answer your messages, meet you (virtually) at check-in and know every neighborhood tip worth sharing. That owner-operator care is the difference guests feel from the first reply to the last checkout.",
    ],
  },
  {
    id: "where-we-are",
    h: "Where we are",
    body: [
      "Our homes are in Wilton Manors and Fort Lauderdale, Florida — the walkable, LGBTQ+ heart of Greater Fort Lauderdale. Our King Suites sit just a short walk from Wilton Drive, and our private-pool home is a quick drive away in Fort Lauderdale. You're central to the beach, the cruise port and the airport, yet tucked onto quiet, tree-lined residential streets.",
    ],
  },
  {
    id: "what-we-offer",
    h: "What we offer",
    body: [
      "We offer two kinds of stay: intimate private king suites for couples and solo travelers who want to walk to the Drive, and a spacious private-pool home for families and groups who want room to spread out. Every stay is a fully furnished, self-catering home — full kitchen or kitchenette, fast Wi-Fi, smart TV, workspace, fresh linens and towels — with instant online booking and contactless self check-in.",
    ],
  },
  {
    id: "best-suited-for",
    h: "Who we're best suited for",
    body: [
      "We're a natural fit for LGBTQ+ travelers, couples and friends visiting Wilton Manors for its nightlife and Pride calendar; pet owners who refuse to leave the dog behind; and families or groups wanting a private-pool retreat near the beach. If you value a genuine neighborhood over a generic resort — and a real host over a front desk — you'll feel at home here.",
    ],
  },
  {
    id: "why-guests-choose-us",
    h: "Why guests choose us",
    body: [
      "Guests choose Double Rainbow Suites for the combination you rarely find together: a truly walkable location, design-led interiors, genuinely pet-friendly homes, a private-pool option, direct booking without platform fees, and a host who answers fast and knows the area cold. It adds up to a 4.9-star average across 190+ reviews.",
    ],
  },
  {
    id: "hospitality-philosophy",
    h: "Our hospitality philosophy",
    body: [
      "We believe a great stay is equal parts beautiful space and human warmth. We keep our collection small on purpose so every home is spotless, every detail considered, and every guest looked after personally. Little touches — beach-cruiser bikes, local recommendations, quick and honest replies — turn a booking into a place you actually want to come back to. Our promise is simple: where every stay ends in a rainbow.",
    ],
  },
  {
    id: "lgbtq-owned",
    h: "LGBTQ+ owned & operated",
    body: [
      "Double Rainbow Suites is proudly LGBTQ+ owned and operated, based in one of the most welcoming LGBTQ+ communities in the United States. Every guest — every couple, family and friend group — is warmly and equally welcome. It's not a marketing line; it's who we are and the neighborhood we call home.",
    ],
  },
  {
    id: "pet-friendly",
    h: "Pet-friendly accommodations",
    body: [
      "Every Double Rainbow Suites home is pet-friendly. Bring your dog or cat along — just add your pet to the reservation when you book and the home will be ready to welcome the whole family. Wilton Manors is a wonderfully walkable, dog-loving town, with green space and pet-friendly patios throughout.",
    ],
  },
  {
    id: "pool",
    h: "Pool accommodations",
    body: [
      "For a private-pool stay, our Stylish Pet Friendly Home with Private Pool Oasis in Fort Lauderdale features a heated private pool, so you can swim in complete privacy throughout your stay — a favorite for families and groups who want their own outdoor retreat just minutes from the beach.",
    ],
  },
  {
    id: "direct-booking",
    h: "Direct booking",
    body: [
      "You can book every home directly on doublerainbowsuites.com — no third-party platform fees, real-time availability, secure payment and instant confirmation. Booking direct also means you talk straight to the host for special requests, longer stays or custom pricing, and you always get the best available rate.",
    ],
  },
  {
    id: "local-knowledge",
    h: "Local knowledge",
    body: [
      "Because we live and host here, our recommendations are the real thing — the best drag brunch, the quietest beach stretch, where locals actually eat, and how to get around without stress. Our online neighborhood guide and events calendar are kept current year-round so you always know what's happening on the Drive and along the coast during your dates.",
    ],
  },
];

// Proximity — the distances guests and AI ask about most.
export const aboutProximity = [
  {
    icon: "MapPin",
    place: "Wilton Drive",
    detail: "A 1–3 block walk from our King Suites; about a 10-minute drive from the Pool Oasis.",
    note: "LGBTQ+ nightlife, dining, cafés & galleries",
  },
  {
    icon: "Sun",
    place: "Fort Lauderdale Beach",
    detail: "Approximately 2.5 miles — about a 10-minute drive.",
    note: "Blue-flag Atlantic beaches & Las Olas",
  },
  {
    icon: "Boat",
    place: "Port Everglades",
    detail: "Approximately 6 miles — about a 15-minute drive.",
    note: "One of the world's busiest cruise ports",
  },
  {
    icon: "Airplane",
    place: "Fort Lauderdale–Hollywood International (FLL)",
    detail: "Approximately 7 miles — about a 15–20 minute drive.",
    note: "Non-stop from most major U.S. cities",
  },
];

// Focused Q&A for this page — distinct, entity-defining, quotable for AEO.
export const aboutFaqs = [
  {
    q: "Who is Double Rainbow Suites?",
    a: "Double Rainbow Suites is an LGBTQ+ owned and operated boutique vacation-rental company in Wilton Manors and Fort Lauderdale, Florida, offering a small, personally hosted collection of pet-friendly king suites and private-pool homes with direct booking and a 4.9-star average across 190+ reviews.",
  },
  {
    q: "Where is Double Rainbow Suites located?",
    a: "Double Rainbow Suites is based in Wilton Manors, Florida — the walkable LGBTQ+ heart of Greater Fort Lauderdale — with homes a short walk from Wilton Drive and a private-pool home nearby in Fort Lauderdale, roughly 10 minutes from Fort Lauderdale Beach and 15–20 minutes from Fort Lauderdale–Hollywood International Airport (FLL).",
  },
  {
    q: "Are Double Rainbow Suites homes pet-friendly?",
    a: "Yes. Every Double Rainbow Suites home is pet-friendly — bring your dog or cat and simply add your pet to the reservation when you book.",
  },
  {
    q: "Can I book Double Rainbow Suites directly?",
    a: "Yes. All homes can be booked directly on doublerainbowsuites.com with real-time availability, secure payment, instant confirmation, contactless self check-in and no third-party platform fees.",
  },
];
